// src/pages/Plantation/tabs/TransportTab.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2, Trash2, X, Truck, MapPin } from 'lucide-react';
import {
  plantationService,
  PlantationTransport,
  PlantationCampagne,
  PlantationVehicule,
} from '../../../services/plantation.service';

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const TYPE_BADGE: Record<string, string> = {
  personnel:  'bg-blue-100 text-blue-700',
  outillage:  'bg-amber-100 text-amber-700',
  materiel:   'bg-orange-100 text-orange-700',
  intrant:    'bg-lime-100 text-lime-700',
  recolte:    'bg-emerald-100 text-emerald-700',
  autre:      'bg-gray-100 text-gray-600',
};

const TYPE_LABEL: Record<string, string> = {
  personnel:  'Personnel',
  outillage:  'Outillage',
  materiel:   'Matériel',
  intrant:    'Intrant',
  recolte:    'Récolte',
  autre:      'Autre',
};

const STATUT_BADGE: Record<string, string> = {
  planifie: 'bg-gray-100 text-gray-600',
  en_cours: 'bg-blue-100 text-blue-700',
  termine:  'bg-emerald-100 text-emerald-700',
  annule:   'bg-red-100 text-red-700',
};

const STATUT_LABEL: Record<string, string> = {
  planifie: 'Planifié',
  en_cours: 'En cours',
  termine:  'Terminé',
  annule:   'Annulé',
};

// ── KPI Card ──────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 truncate">{label}</p>
      <p className="text-lg font-bold text-gray-800 leading-tight">{value}</p>
    </div>
  </div>
);

// ── Modal Création / Édition ───────────────────────────────────────────────────

interface ModalTransportProps {
  entiteId: string;
  campagnes: PlantationCampagne[];
  vehicules: PlantationVehicule[];
  editing?: PlantationTransport | null;
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = (): Partial<PlantationTransport> => ({
  date: new Date().toISOString().slice(0, 10),
  immatriculation: '',
  chauffeurMatricule: '',
  typeTransport: 'recolte',
  statut: 'planifie',
});

const ModalTransport: React.FC<ModalTransportProps> = ({
  entiteId, campagnes, vehicules, editing, onClose, onSaved,
}) => {
  const [form, setForm] = useState<Partial<PlantationTransport>>(
    editing ? { ...editing } : emptyForm(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k: keyof PlantationTransport, v: any) =>
    setForm(f => ({ ...f, [k]: v }));

  // Calcul km parcourus temps réel
  const kmParcouru =
    (form.kmFin != null && form.kmDebut != null && form.kmFin > form.kmDebut)
      ? form.kmFin - form.kmDebut
      : null;

  const handleVehiculeSelect = (id: string) => {
    const v = vehicules.find(veh => veh.id === id);
    if (v) {
      set('vehiculeId', v.id);
      set('immatriculation', v.immatriculation);
    } else {
      set('vehiculeId', undefined);
    }
  };

  const handleSave = async () => {
    setError('');
    if (!form.date || !form.immatriculation || !form.chauffeurMatricule || !form.typeTransport) {
      setError('Date, immatriculation, matricule chauffeur et type sont obligatoires.');
      return;
    }
    setLoading(true);
    try {
      const payload: Partial<PlantationTransport> = {
        ...form,
        entiteId,
        kmParcouru: kmParcouru ?? form.kmParcouru,
      };
      if (editing) {
        await plantationService.updateTransport(editing.id, payload);
      } else {
        await plantationService.createTransport(payload);
      }
      onSaved();
      onClose();
    } catch {
      setError('Erreur lors de l\'enregistrement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            {editing ? 'Modifier le mouvement transport' : 'Nouveau mouvement transport'}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">
              {error}
            </div>
          )}

          {/* Identification du véhicule */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Identification du véhicule</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.date || ''} onChange={e => set('date', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type transport *</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.typeTransport || 'recolte'} onChange={e => set('typeTransport', e.target.value as PlantationTransport['typeTransport'])}>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Immatriculation *</label>
                {vehicules.length > 0 ? (
                  <div className="space-y-1">
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={form.vehiculeId || ''}
                      onChange={e => handleVehiculeSelect(e.target.value)}>
                      <option value="">— Saisie libre —</option>
                      {vehicules.map(v => (
                        <option key={v.id} value={v.id}>{v.immatriculation}{v.marque ? ` (${v.marque})` : ''}</option>
                      ))}
                    </select>
                    {!form.vehiculeId && (
                      <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="Saisir l'immatriculation"
                        value={form.immatriculation || ''}
                        onChange={e => set('immatriculation', e.target.value)} />
                    )}
                  </div>
                ) : (
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Ex: AB 1234 CI"
                    value={form.immatriculation || ''}
                    onChange={e => set('immatriculation', e.target.value)} />
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Matricule chauffeur *</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Ex: CH-0042"
                  value={form.chauffeurMatricule || ''}
                  onChange={e => set('chauffeurMatricule', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Mission */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Mission</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Activité</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Ex: Transport de régimes"
                  value={form.activite || ''}
                  onChange={e => set('activite', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Campagne</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.campagneId || ''}
                  onChange={e => set('campagneId', e.target.value || undefined)}>
                  <option value="">— Aucune —</option>
                  {campagnes.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Itinéraire prévu</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Ex: Bloc A → Usine"
                  value={form.itinerairePrev || ''}
                  onChange={e => set('itinerairePrev', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Itinéraire réalisé</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Ex: Bloc A → Usine"
                  value={form.itineraireRealise || ''}
                  onChange={e => set('itineraireRealise', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Métriques */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Métriques</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Heure départ</label>
                <input type="time" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.heureDepart || ''}
                  onChange={e => set('heureDepart', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Heure arrivée</label>
                <input type="time" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.heureArrivee || ''}
                  onChange={e => set('heureArrivee', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Km début</label>
                <input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.kmDebut ?? ''}
                  onChange={e => set('kmDebut', e.target.value ? parseFloat(e.target.value) : undefined)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Km fin</label>
                <input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.kmFin ?? ''}
                  onChange={e => set('kmFin', e.target.value ? parseFloat(e.target.value) : undefined)} />
              </div>
            </div>
            {kmParcouru !== null && (
              <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-700 font-medium">
                Km parcourus : {kmParcouru} km
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Heures moteur</label>
                <input type="number" step="0.1" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.heureMoteur ?? ''}
                  onChange={e => set('heureMoteur', e.target.value ? parseFloat(e.target.value) : undefined)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Charge (kg)</label>
                <input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.chargementKg ?? ''}
                  onChange={e => set('chargementKg', e.target.value ? parseFloat(e.target.value) : undefined)} />
              </div>
            </div>
          </div>

          {/* Autres */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Autres</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.statut || 'planifie'}
                  onChange={e => set('statut', e.target.value as PlantationTransport['statut'])}>
                  <option value="planifie">Planifié</option>
                  <option value="en_cours">En cours</option>
                  <option value="termine">Terminé</option>
                  <option value="annule">Annulé</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Observations</label>
                <textarea rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  value={form.observations || ''}
                  onChange={e => set('observations', e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Annuler
          </button>
          <button onClick={handleSave} disabled={loading}
            className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
            {loading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── TransportTab ───────────────────────────────────────────────────────────────

interface Props {
  entiteId: string;
  campagnes: PlantationCampagne[];
  vehicules: PlantationVehicule[];
  campagneActive?: PlantationCampagne;
}

const TransportTab: React.FC<Props> = ({ entiteId, campagnes, vehicules, campagneActive }) => {
  const [transports, setTransports] = useState<PlantationTransport[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PlantationTransport | null>(null);

  // Filtres
  const [filtreDate, setFiltreDate] = useState(new Date().toISOString().slice(0, 10));
  const [filtreType, setFiltreType] = useState('');
  const [filtreCampagne, setFiltreCampagne] = useState(campagneActive?.id || '');

  const load = useCallback(async () => {
    if (!entiteId) return;
    setLoading(true);
    try {
      const data = await plantationService.listTransports(entiteId, {
        date: filtreDate || undefined,
        typeTransport: filtreType || undefined,
        campagneId: filtreCampagne || undefined,
      });
      setTransports(data);
    } catch {
      // silencieux
    } finally {
      setLoading(false);
    }
  }, [entiteId, filtreDate, filtreType, filtreCampagne]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce mouvement transport ?')) return;
    try {
      await plantationService.deleteTransport(id);
      setTransports(t => t.filter(x => x.id !== id));
    } catch {
      // silencieux
    }
  };

  // KPIs
  const totalKm = transports.reduce((s, t) => s + (t.kmParcouru || 0), 0);
  const totalHMoteur = transports.reduce((s, t) => s + (t.heureMoteur || 0), 0);
  const enCours = transports.filter(t => t.statut === 'en_cours').length;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3">
        <KpiCard label="Total mouvements" value={transports.length} icon={Truck} color="bg-gray-100 text-gray-600" />
        <KpiCard label="Km parcourus" value={`${totalKm.toLocaleString('fr-FR')} km`} icon={MapPin} color="bg-blue-100 text-blue-700" />
        <KpiCard label="Heures moteur" value={`${totalHMoteur.toFixed(1)} h`} icon={RefreshCw} color="bg-amber-100 text-amber-700" />
        <KpiCard label="En cours" value={enCours} icon={Truck} color="bg-emerald-100 text-emerald-700" />
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date</label>
          <input type="date" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            value={filtreDate} onChange={e => setFiltreDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type transport</label>
          <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            value={filtreType} onChange={e => setFiltreType(e.target.value)}>
            <option value="">Tous</option>
            {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Campagne</label>
          <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            value={filtreCampagne} onChange={e => setFiltreCampagne(e.target.value)}>
            <option value="">Toutes</option>
            {campagnes.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
          </select>
        </div>
        <button onClick={load}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Filtrer
        </button>
        <div className="ml-auto">
          <button
            onClick={() => { setEditing(null); setShowModal(true); }}
            className="bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-emerald-700 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Nouveau mouvement
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                {['Date', 'Immatriculation', 'Chauffeur', 'Type', 'Activité', 'Itinéraire prévu', 'Km parcouru', 'H. moteur', 'Charge (kg)', 'Statut', 'Actions'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={11} className="text-center py-8 text-gray-400">Chargement…</td></tr>
              ) : transports.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-10 text-gray-400">
                    <Truck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    Aucun mouvement transport enregistré
                  </td>
                </tr>
              ) : transports.map(t => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">{fmtDate(t.date)}</td>
                  <td className="px-3 py-2.5 font-medium text-gray-800">{t.immatriculation}</td>
                  <td className="px-3 py-2.5 text-gray-600 text-xs">{t.chauffeurMatricule}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${TYPE_BADGE[t.typeTransport] || 'bg-gray-100 text-gray-600'}`}>
                      {TYPE_LABEL[t.typeTransport] || t.typeTransport}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 text-xs max-w-[120px] truncate">{t.activite || '—'}</td>
                  <td className="px-3 py-2.5 text-gray-600 text-xs max-w-[140px] truncate">{t.itinerairePrev || '—'}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">{t.kmParcouru != null ? `${t.kmParcouru} km` : '—'}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">{t.heureMoteur != null ? `${t.heureMoteur} h` : '—'}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">{t.chargementKg != null ? `${t.chargementKg}` : '—'}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${STATUT_BADGE[t.statut] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUT_LABEL[t.statut] || t.statut}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1">
                      <button
                        onClick={() => { setEditing(t); setShowModal(true); }}
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600"
                        title="Modifier">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600"
                        title="Supprimer">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <ModalTransport
          entiteId={entiteId}
          campagnes={campagnes}
          vehicules={vehicules}
          editing={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default TransportTab;
